export interface AgentConnectionBinding {
  agentId: string
  language: string
  realtimeProvider: string
  avatarId: string
  avatarType: string
  voiceId: string
}

export interface DestroyableConnection {
  destroy(): void
}

export function connectionBindingKey(binding: AgentConnectionBinding | null): string | null {
  if (!binding) return null
  return JSON.stringify([
    binding.agentId,
    binding.language,
    binding.realtimeProvider,
    binding.avatarId,
    binding.avatarType,
    binding.voiceId,
  ])
}

export function connectionBindingsEqual(
  first: AgentConnectionBinding | null,
  second: AgentConnectionBinding | null,
): boolean {
  if (first === second) return true
  return Boolean(first && second && connectionBindingKey(first) === connectionBindingKey(second))
}

/** Tracks the resource and identity that a singleton connection belongs to. */
export class BoundConnectionRegistry<T extends DestroyableConnection> {
  private resource: T | null = null
  private binding: AgentConnectionBinding | null = null

  getResource(): T | null {
    return this.resource
  }

  getBinding(): AgentConnectionBinding | null {
    return this.binding ? { ...this.binding } : null
  }

  getReusable(binding: AgentConnectionBinding | null): T | null {
    return connectionBindingsEqual(this.binding, binding) ? this.resource : null
  }

  replace(resource: T, binding: AgentConnectionBinding | null): void {
    if (this.resource && this.resource !== resource) {
      this.resource.destroy()
    }
    this.resource = resource
    this.binding = binding ? { ...binding } : null
  }

  clearIf(resource: T): boolean {
    if (this.resource !== resource) return false
    this.clear()
    return true
  }

  clear(): void {
    const resource = this.resource
    this.resource = null
    this.binding = null
    resource?.destroy()
  }
}
